import { useEffect, useRef, useState } from "react";
import { X, Plus } from "lucide-react";
import type { QuizQuestion } from "@shared/types";
import { SheetModal } from "../SheetModal";
import { Pill } from "../ux/Pill";

/**
 * The bottom-sheet selectors used across the profile editor. They all share the
 * same chrome — an ✕ to dismiss, a centered title, and a "Save" that commits the
 * draft — and come in three flavours:
 *   • TagPickerSheet  — multi-select tags with suggestions + add-your-own
 *   • TextFieldSheet  — a single free-text value
 *   • OptionPickerSheet — single/multiple choice from a fixed option set
 */

/** Order-insensitive equality for the tag/option draft vs. saved value. */
function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function SelectorHeader({
  title,
  onClose,
  onSave,
  saveDisabled,
}: {
  title: string;
  onClose: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
}) {
  return (
    <div className="relative flex items-center justify-center px-4 pt-5 pb-4 shrink-0">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute left-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
        {title}
      </h2>
      <button
        type="button"
        onClick={onSave}
        disabled={saveDisabled}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-primary-600 dark:text-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
      >
        Save
      </button>
    </div>
  );
}

/** The big rounded "pill" input shared by the tag + text sheets. */
function PillInput({
  inputRef,
  value,
  onChange,
  onEnter,
  onClear,
  placeholder,
  maxLength,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  value: string;
  onChange: (v: string) => void;
  onEnter?: () => void;
  onClear: () => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onEnter?.();
          }
        }}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full rounded-full bg-neutral-100 dark:bg-neutral-800 px-5 py-3.5 pr-11 text-[15px] text-neutral-900 dark:text-neutral-100 outline-none focus:ring-2 focus:ring-primary-500/30 placeholder:text-neutral-400"
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ─── Tag picker ──────────────────────────────────────────────────────────────

export function TagPickerSheet({
  isOpen,
  onClose,
  title,
  value,
  onSave,
  suggestions,
  max = 12,
  placeholder = "Search or add your own…",
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  value: string[];
  onSave: (tags: string[]) => void;
  suggestions: string[];
  max?: number;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState<string[]>(value);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDraft(value);
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const atLimit = draft.length >= max;
  const q = query.trim();
  const has = (t: string) => draft.some((x) => x.toLowerCase() === t.toLowerCase());

  const toggle = (t: string) => {
    if (has(t)) setDraft(draft.filter((x) => x.toLowerCase() !== t.toLowerCase()));
    else if (!atLimit) setDraft([...draft, t]);
  };

  const addCustom = () => {
    const t = q.replace(/^#/, "").trim();
    if (!t || has(t) || atLimit) return;
    setDraft((prev) => [...prev, t]);
    setQuery("");
  };

  const filtered = suggestions.filter(
    (t) => (!q || t.toLowerCase().includes(q.toLowerCase())) && !has(t),
  );
  const exactExists =
    suggestions.some((t) => t.toLowerCase() === q.toLowerCase()) || has(q);

  const changed = !sameSet(draft, value);
  const save = () => {
    onSave(draft);
    onClose();
  };

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg h-[85vh] sm:h-[78vh]">
      <SelectorHeader title={title} onClose={onClose} onSave={save} saveDisabled={!changed} />

      <div className="px-4 pb-3 shrink-0">
        <PillInput
          inputRef={inputRef}
          value={query}
          onChange={setQuery}
          onEnter={addCustom}
          onClear={() => setQuery("")}
          placeholder={placeholder}
          maxLength={40}
        />
      </div>

      <div className="h-px bg-neutral-200 dark:bg-neutral-800 mx-4 shrink-0" />

      <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
        {/* Your picks — soft primary pills with ✕ */}
        {draft.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {draft.map((t) => (
              <Pill
                key={t}
                variant="soft"
                size="sm"
                onClick={() => toggle(t)}
                trailing={<X className="w-3.5 h-3.5 opacity-70" />}
              >
                {t}
              </Pill>
            ))}
          </div>
        )}

        {/* Suggestions — wrapping cloud of outline pills with + */}
        <div className="flex flex-wrap justify-center gap-2.5">
          {q && !exactExists && !atLimit && (
            <Pill
              variant="outline"
              onClick={addCustom}
              trailing={<Plus className="w-4 h-4 text-neutral-400" />}
            >
              {q}
            </Pill>
          )}
          {filtered.map((t) => (
            <Pill
              key={t}
              variant="outline"
              disabled={atLimit}
              onClick={() => toggle(t)}
              trailing={<Plus className="w-4 h-4 text-neutral-400" />}
            >
              {t}
            </Pill>
          ))}
          {atLimit && (
            <p className="text-xs text-neutral-400 mt-1">That's the max of {max}.</p>
          )}
        </div>
      </div>
    </SheetModal>
  );
}

// ─── Free-text field ─────────────────────────────────────────────────────────

export function TextFieldSheet({
  isOpen,
  onClose,
  title,
  value,
  onSave,
  placeholder = "",
  maxLength = 120,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  value: string | null;
  onSave: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDraft(value ?? "");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const changed = draft.trim() !== (value ?? "").trim();
  const save = () => {
    onSave(draft.trim());
    onClose();
  };

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg">
      <SelectorHeader title={title} onClose={onClose} onSave={save} saveDisabled={!changed} />
      <div className="px-4 pt-1 pb-8">
        <PillInput
          inputRef={inputRef}
          value={draft}
          onChange={setDraft}
          onEnter={() => changed && save()}
          onClear={() => setDraft("")}
          placeholder={placeholder}
          maxLength={maxLength}
        />
      </div>
    </SheetModal>
  );
}

// ─── Option picker (single / multiple) ───────────────────────────────────────

export function OptionPickerSheet({
  isOpen,
  onClose,
  title,
  question,
  value,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  question: QuizQuestion;
  value: unknown;
  onSave: (v: string | string[]) => void;
}) {
  const multiple = question.type === "multiple";
  const toArray = (v: unknown): string[] =>
    Array.isArray(v) ? (v as string[]) : v == null || v === "" ? [] : [String(v)];

  const [draft, setDraft] = useState<string[]>(toArray(value));

  useEffect(() => {
    if (isOpen) setDraft(toArray(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const max = question.maxSelections;
  const toggle = (v: string) => {
    if (multiple) {
      if (draft.includes(v)) setDraft(draft.filter((x) => x !== v));
      else if (!max || draft.length < max) setDraft([...draft, v]);
    } else {
      setDraft([v]);
    }
  };

  const changed = !sameSet(draft, toArray(value));
  const save = () => {
    onSave(multiple ? draft : draft[0] ?? "");
    onClose();
  };

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg">
      <SelectorHeader title={title} onClose={onClose} onSave={save} saveDisabled={!changed} />
      <div className="px-4 pt-1 pb-8 overflow-y-auto">
        <div className="flex flex-wrap justify-center gap-2.5">
          {question.options?.map((o) => {
            const selected = draft.includes(o.value);
            return (
              <Pill
                key={o.value}
                variant={selected ? "solid" : "outline"}
                onClick={() => toggle(o.value)}
                aria-pressed={selected}
                leading={o.emoji ? <span>{o.emoji}</span> : undefined}
              >
                {o.label}
              </Pill>
            );
          })}
        </div>
        {multiple && max && (
          <p className="text-center text-xs text-neutral-400 mt-4">
            Pick up to {max}.
          </p>
        )}
      </div>
    </SheetModal>
  );
}
